import { LockIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import useResizedArtwork from "../hooks/useResizedArtwork";
import type { SeriesBts } from "../hooks/useBehindTheScenes";
import ExclusiveBadge from "@/components/ExclusiveBadge";
import FadeInImage from "@/components/FadeInImage";
import usePremium from "@/hooks/usePremium";
import { cn } from "@/lib/utils";

/**
 * A Behind the Scenes video (SHO-757): its portrait thumbnail and caption.
 * A button either way — it opens the player for a VIP, the paywall for a
 * free user, whose thumbnail is blurred under a lock (the server sends no
 * `url` then, so nothing could play anyway). A VIP's video still `locked`
 * is the server catching up (the backoff in useBehindTheScenes), nothing to
 * sell: the tile waits, disabled, unblurred and lock-less.
 */
const BtsTile: React.FC<{ bts: SeriesBts; onClick: () => void }> = ({
  bts,
  onClick,
}) => {
  const { t } = useTranslation();
  const thumbnail = useResizedArtwork(bts.thumbnail.original, "640");
  const { isPremiumUser } = usePremium();
  const pending = bts.locked && isPremiumUser;
  const locked = bts.locked && !isPremiumUser;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={pending}
      data-test-id={`bts-tile-${bts.id}`}
      data-locked={locked || undefined}
      data-pending={pending || undefined}
      className="group/tile flex flex-col gap-2 text-left text-white disabled:cursor-wait"
    >
      <span className="bg-neutral-60 relative block aspect-9/16 w-full overflow-hidden rounded-lg">
        {/* Loading state, under the image, as on the episode tiles. */}
        <ExclusiveBadge className="absolute inset-0 m-auto size-8 text-neutral-50" />
        <FadeInImage
          {...thumbnail}
          alt=""
          loading="lazy"
          className={cn(
            "relative h-full w-full object-cover",
            locked && "scale-110 blur-md",
          )}
        />
        {locked && (
          <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/30 px-4 text-center text-sm font-semibold">
            <LockIcon className="size-5" />
            <span className="md:hidden">
              {t("series_details_bts_locked_label_web")}
            </span>
          </span>
        )}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg inset-shadow-[0_0_0_1px] inset-shadow-white/20 transition-shadow duration-300 ease-out group-hover/tile:inset-shadow-white/40"
        />
      </span>
      <span className="line-clamp-2 text-sm">{bts.displayTitle}</span>
    </button>
  );
};

export default BtsTile;
